import React, { useCallback, useMemo, useState } from "react";
import QATable, { ColumnDefinition } from "../../components/Design/Table/QATable";
import { ControllerAPI, ExecutionRoleRO, RoleRO, WorkerRO, WorkerState } from "../../api/api";
import { generateToString } from "../../util/StringUtils";
import { makeStyles } from "@mui/styles";
import ColorDotLegend from "../../components/Data/ColorDotLegend";
import QATableButtonRow from "../../components/Design/Table/Layout/QATableButtonRow";
import QATableButton from "../../components/Design/Table/Controls/QATableButton";
import { PlayArrow, ScreenshotMonitor, Stop, Subject } from "@mui/icons-material";
import useDialog from "../../components/Hooks/Dialog/useDialog";
import ConfirmationDialog from "../../components/Hooks/Dialog/ConfirmationDialog";
import { hasFailed } from "@miragon/react-utils-api";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import { updateExecutionsManually } from "../../state/ExecutionsState";
import { useAppDispatch } from "../../state/Store";
import QAButtonRow from "../../components/Design/Layout/QAButtonRow";
import QAButton from "../../components/Design/Controls/QAButton";
import WorkerScreenshotDialog from "../Worker/WorkerScreenshotDialog";

interface Props {
    executionId: string;
    roles: ExecutionRoleRO[];
    availableRoles: RoleRO[];
    workers: WorkerRO[];
}

export const stateToString = generateToString<WorkerState>({
    IDLE: "Bereit",
    WORKING: "Arbeitet"
});

const useStyles = makeStyles(() => ({
    category: {
        height: "100%",
        width: "calc(100% + 16px)",
        display: "flex",
        alignItems: "center",
        fontWeight: 500,
        padding: "0 1rem",
        marginLeft: "-16px"
    },
    legend: {
        marginBottom: "1rem",
        marginTop: 0
    },
    topBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "0.5rem"
    }
}));

const ExecutionRoleTable: React.FC<Props> = props => {
    const classes = useStyles();
    const dialog = useDialog();
    const snackbar = useSnackbar();
    const dispatch = useAppDispatch();

    const { availableRoles, workers, roles, executionId } = props;

    const [selectedRows, setSelectedRows] = useState<string[]>([]);

    const onOpenLogs = useCallback(async (workerId: string) => {
        const response = await ControllerAPI.getWorkerLogsUrl({ workerId });
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            window.open(response.result, "_blank");
        }
    }, [snackbar]);

    const onCaptureScreenshot = useCallback(async (workerId: string) => {
        dialog.open(WorkerScreenshotDialog, {
            workerId
        });
    }, [dialog]);

    const onEnableDisable = useCallback(async (items: ExecutionRoleRO[]) => {
        const result = await dialog.open(ConfirmationDialog, {
            title: items[0].disabled ? "Rolle aktivieren" : "Rolle deaktivieren",
            question: `Wollen Sie die Rollen wirklich ${items[0].disabled ? "aktivieren" : "deaktivieren"}?`,
            acceptButtonTitle: items[0].disabled ? "Aktivieren" : "Deaktivieren"
        });
        if (!result.accepted) {
            return;
        }

        const response = items[0].disabled
            ? await ControllerAPI.enableExecutionRoles({
                executionId: executionId,
                executionRoleIds: items.map(i => i.id)
            })
            : await ControllerAPI.disableExecutionRoles({
                executionId: executionId,
                executionRoleIds: items.map(i => i.id)
            });

        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateExecutionsManually({
                update: [response.result]
            }));
        }
    }, [dialog, dispatch, executionId, snackbar]);

    const columns: ColumnDefinition<ExecutionRoleRO>[] = useMemo(() => [
        {
            id: "role",
            title: "Rolle",
            width: "auto",
            sortable: true,
            getValue: item => `${availableRoles.find(r => r.id === item.roleId)?.name} v${item.roleVersion}`
        },
        {
            id: "state",
            title: "Status",
            width: "150px",
            sortable: true,
            getValue: item => item.disabled,
            renderCell: item => item.disabled ? (
                <i>Deaktiviert</i>
            ) : null
        },
        {
            id: "worker",
            title: "Worker",
            width: "500px",
            padding: {
                left: 16,
                top: 0,
                bottom: 0,
                right: 0
            },
            sortable: true,
            getValue: item => workers.find(w => w.workerAssignment?.executionRoleId === item.id)?.id,
            renderCell: item => {
                const worker = workers.find(w => w.workerAssignment?.executionRoleId === item.id);
                return (
                    <div
                        className={classes.category}
                        style={{
                            color: worker ? "white" : "black",
                            backgroundColor: worker ? "green" : "lightgrey"
                        }}>
                        {worker?.id ?? "Keiner"}
                    </div>
                );
            }
        },
        {
            id: "actions",
            width: "100px",
            padding: {
                top: 0,
                bottom: 0,
                left: 16,
                right: 16
            },
            getValue: () => undefined,
            renderCell: item => (
                <QATableButtonRow>
                    <QATableButton
                        icon={item.disabled ? PlayArrow : Stop}
                        onClicked={() => onEnableDisable([item])} />
                    <QATableButton
                        icon={Subject}
                        disabled={!workers.find(w => w.workerAssignment?.executionRoleId === item.id)}
                        onClicked={() => onOpenLogs(workers.find(w => w.workerAssignment?.executionRoleId === item.id)?.id ?? "")} />
                    <QATableButton
                        icon={ScreenshotMonitor}
                        disabled={!workers.find(w => w.workerAssignment?.executionRoleId === item.id)}
                        onClicked={() => onCaptureScreenshot(workers.find(w => w.workerAssignment?.executionRoleId === item.id)?.id ?? "")} />
                </QATableButtonRow>
            )
        }
    ], [availableRoles, classes.category, onCaptureScreenshot, onEnableDisable, onOpenLogs, workers]);

    return (
        <>
            <div className={classes.topBar}>
                <QAButtonRow>
                    <QAButton
                        light
                        size="small"
                        title={roles.filter(r => selectedRows.includes(r.id) && r.disabled).length + " aktivieren"}
                        onClicked={() => onEnableDisable(roles.filter(r => selectedRows.includes(r.id) && r.disabled))}
                        disabled={roles.filter(r => selectedRows.includes(r.id) && r.disabled).length === 0} />
                    <QAButton
                        light
                        size="small"
                        title={roles.filter(r => selectedRows.includes(r.id) && !r.disabled).length + " deaktivieren"}
                        onClicked={() => onEnableDisable(roles.filter(r => selectedRows.includes(r.id) && !r.disabled))}
                        disabled={roles.filter(r => selectedRows.includes(r.id) && !r.disabled).length === 0} />
                </QAButtonRow>
                <ColorDotLegend
                    className={classes.legend}
                    items={[
                        {
                            color: "green",
                            name: roles.filter(r => !!workers.find(w => w.workerAssignment?.executionRoleId === r.id)).length + " Arbeitet"
                        },
                        {
                            color: "lightgrey",
                            name: roles.filter(r => !workers.find(w => w.workerAssignment?.executionRoleId === r.id)).length + " Bereit"
                        }
                    ]} />
            </div>
            <QATable
                onRowSelectionChanged={setSelectedRows}
                defaultSortByColumn="role"
                items={roles}
                defaultPageSize={25}
                getId={item => item.id}
                fallbackText="Keine Rollen gefunden."
                columns={columns} />
        </>
    );
};

export default ExecutionRoleTable;
